import React, { useEffect, useState } from "react";
import { getTrainingDays } from "../../services/authService";

function startOfWeek(date) {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	const day = d.getDay(); // 0 = dim
	const diff = day === 0 ? -6 : 1 - day;
	d.setDate(d.getDate() + diff);
	return d;
}

function endOfWeek(monday) {
	const d = new Date(monday);
	d.setDate(d.getDate() + 6);
	d.setHours(23, 59, 59, 999);
	return d;
}

function fmtRange(monday) {
	const sunday = endOfWeek(monday);
	const opt = { day: "2-digit", month: "short" };
	return `${monday.toLocaleDateString("fr-FR", opt)} → ${sunday.toLocaleDateString("fr-FR", opt)}`;
}

// Charge les jours d'entrainement de chaque élève pour la semaine et
// détecte ceux qui n'ont rien de programmé.
async function fetchUnplannedStudents(students, monday) {
	const sunday = endOfWeek(monday);
	// Cache par (studentId, year, month)
	const cache = new Map();
	const unplanned = [];
	for (const s of students) {
		let hasAny = false;
		// La semaine peut chevaucher 2 mois : on interroge lundi et dimanche.
		const monthsToCheck = new Set();
		monthsToCheck.add(`${monday.getFullYear()}-${monday.getMonth() + 1}`);
		monthsToCheck.add(`${sunday.getFullYear()}-${sunday.getMonth() + 1}`);
		for (const monthKey of monthsToCheck) {
			const [y, m] = monthKey.split("-").map(Number);
			const key = `${s.id}-${monthKey}`;
			if (!cache.has(key)) {
				const res = await getTrainingDays(s.id, y, m);
				cache.set(key, res.success ? (res.trainingDays || []) : []);
			}
			const days = cache.get(key);
			// Un jour X du mois M/Y appartient à la semaine si sa date est entre monday et sunday
			for (const dayNum of days) {
				const d = new Date(y, m - 1, dayNum, 12, 0, 0);
				if (d >= monday && d <= sunday) { hasAny = true; break; }
			}
			if (hasAny) break;
		}
		if (!hasAny) unplanned.push(s);
	}
	return unplanned;
}

const CHIP_HINT_STORAGE_KEY = "coach_widget_chip_hint_seen";

export default function WeeklyActionCenter({ students, alertsCount = 0, onSelectStudent, onSelectAlerts, onBulkAssign, onDuplicateStudentWeek, refreshKey = 0 }) {
	const [monday] = useState(() => startOfWeek(new Date()));
	const [loading, setLoading] = useState(true);
	const [unplanned, setUnplanned] = useState([]);
	// Le hint sous le titre "Élèves à programmer" n'est utile qu'à la première
	// découverte. On le fait disparaître dès que le coach utilise le bouton ↻
	// (le pattern est alors compris) et on mémorise en localStorage pour ne pas
	// le réafficher aux prochaines sessions.
	const [showHint, setShowHint] = useState(() => {
		try { return localStorage.getItem(CHIP_HINT_STORAGE_KEY) !== "1"; }
		catch { return true; }
	});
	const dismissHint = () => {
		setShowHint(false);
		try { localStorage.setItem(CHIP_HINT_STORAGE_KEY, "1"); } catch {}
	};

	useEffect(() => {
		if (!students || students.length === 0) {
			setUnplanned([]);
			setLoading(false);
			return;
		}
		let cancelled = false;
		setLoading(true);
		fetchUnplannedStudents(students, monday).then((res) => {
			if (cancelled) return;
			setUnplanned(res);
			setLoading(false);
		});
		return () => { cancelled = true; };
		// refreshKey : le parent l'incrémente après un action réussie (ex : duplication
		// de la semaine d'un élève depuis un chip ↻). Ça force le widget à recharger
		// et l'élève programmé disparaît de la liste "à faire".
	}, [students, monday, refreshKey]);

	// Retrait optimiste : dès que le coach clique ↻ pour un élève, on l'enlève
	// immédiatement de la liste sans attendre le refresh serveur. Améliore la
	// perception de réactivité et évite le double-clic accidentel.
	const removeStudentOptimistic = (studentId) => {
		setUnplanned((prev) => prev.filter((s) => s.id !== studentId));
	};

	const nStudents = (students || []).length;
	const nUnplanned = unplanned.length;
	const nPlanned = nStudents - nUnplanned;

	return (
		<div className="weekly-action-center">
			<div className="weekly-action-center-head">
				<div>
					<div className="weekly-action-center-title">À faire cette semaine</div>
					<div className="weekly-action-center-sub">Semaine du {fmtRange(monday)}</div>
				</div>
				{onBulkAssign && (
					<button className="mens-btn primary" onClick={onBulkAssign} style={{ fontSize: 13 }}>
						Affecter un modèle en masse
					</button>
				)}
			</div>

			<div className="weekly-action-center-stats">
				<div className={`wac-stat ${nUnplanned === 0 ? "ok" : "warn"}`}>
					<div className="wac-stat-value">{loading ? "…" : nUnplanned}</div>
					<div className="wac-stat-label">
						{nUnplanned <= 1 ? "élève sans séance" : "élèves sans séance"}
					</div>
				</div>
				<div className="wac-stat">
					<div className="wac-stat-value">{loading ? "…" : nPlanned}</div>
					<div className="wac-stat-label">
						{nPlanned <= 1 ? "élève programmé" : "élèves programmés"}
					</div>
				</div>
				<div className={`wac-stat ${alertsCount > 0 ? "warn" : "ok"}`}>
					<div className="wac-stat-value">{alertsCount}</div>
					<div className="wac-stat-label">
						{alertsCount <= 1 ? "alerte" : "alertes"}
					</div>
				</div>
			</div>

			{!loading && nUnplanned > 0 && (
				<div className="weekly-action-center-list">
					<div className="weekly-action-center-list-head">
						Élèves à programmer
						{showHint && (
							<span className="weekly-action-center-list-hint">
								— clic sur le nom pour ouvrir, sur ↻ pour dupliquer la semaine dernière
							</span>
						)}
					</div>
					<div className="weekly-action-center-chips">
						{unplanned.map((s) => (
							<div key={s.id} className="weekly-action-center-chip-group" role="group">
								<button
									type="button"
									className="weekly-action-center-chip-name"
									onClick={() => onSelectStudent?.(s)}
									title={`Ouvrir le calendrier de ${s.prenom}`}
								>
									{s.prenom} {s.nom?.charAt(0) || ""}.
								</button>
								{onDuplicateStudentWeek && (
									<button
										type="button"
										className="weekly-action-center-chip-action"
										onClick={() => {
											dismissHint();
											// Retrait optimiste avant que la modale s'ouvre : évite que le chip
											// reste affiché pendant que la modale est active. Si l'user annule
											// la modale, il retrouvera l'élève au prochain refresh (10s en auto
											// ou au reload de la page).
											removeStudentOptimistic(s.id);
											onDuplicateStudentWeek(s);
										}}
										title={`Dupliquer la semaine dernière de ${s.prenom}`}
										aria-label={`Dupliquer la semaine dernière de ${s.prenom}`}
									>
										<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M3 3v5h5" />
											<path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
										</svg>
									</button>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			{!loading && nUnplanned === 0 && nStudents > 0 && (
				<div className="weekly-action-center-ok">
					Toutes les séances de la semaine sont programmées 🎯
				</div>
			)}
		</div>
	);
}
